import { useQuery } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";

interface PortalProjectDetail {
  id: string;
  name: string;
  description: string | null;
  status: string;
  clientName: string;
  startDate: string | null;
  targetEndDate: string | null;
  columns: Array<{ id: string; name: string; key: string }>;
  tasks: Array<{
    id: string;
    title: string;
    description: string | null;
    columnId: string;
    assigneeName: string | null;
    dueDate: string | null;
    serviceItemName: string | null;
  }>;
}

function toDateInput(value: string | null) {
  return value ? value.slice(0, 10) : "Not set";
}

export default function ClientProjectDetailPage() {
  const [, navigate] = useLocation();
  const [, params] = useRoute("/portal/projects/:id");
  const projectId = params?.id;

  const { data: project, isLoading } = useQuery<PortalProjectDetail>({
    queryKey: [`/api/portal/projects/${projectId}`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: Boolean(projectId),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[200px] rounded-lg" />
        <Skeleton className="h-[360px] rounded-lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/portal/projects")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to projects
        </Button>
        <p className="text-muted-foreground">Project not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate("/portal/projects")}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to projects
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-2xl">{project.name}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">{project.clientName}</p>
            </div>
            <Badge variant="outline" className="capitalize">
              {project.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {project.description || "No project description has been added yet."}
          </p>
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <p>Start date: {toDateInput(project.startDate)}</p>
            <p>Target end: {toDateInput(project.targetEndDate)}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-4">
        {project.columns.map((column) => (
          <Card key={column.id}>
            <CardHeader>
              <CardTitle className="text-base">{column.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {project.tasks
                .filter((task) => task.columnId === column.id)
                .map((task) => (
                  <div key={task.id} className="rounded-lg border p-3">
                    <p className="font-medium">{task.title}</p>
                    <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                      <p>{task.serviceItemName ? `Service item: ${task.serviceItemName}` : "Planning-only task"}</p>
                      <p>{task.assigneeName ? `Assignee: ${task.assigneeName}` : "Unassigned"}</p>
                      <p>{task.dueDate ? `Due: ${toDateInput(task.dueDate)}` : "No due date"}</p>
                    </div>
                    {task.description && (
                      <p className="mt-2 text-sm text-muted-foreground">{task.description}</p>
                    )}
                  </div>
                ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
